import { useMemo, useState } from 'react';
import type { DocumentData } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Copy, ServerCog } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertBanner } from '@/components/AlertBanner';
import { useT } from '@/lib/i18n';
import { fmtBytes } from '@/lib/utils';
import { parseRemoteInventory, remoteFileTotals, type RemoteInventory } from '@/lib/fleet';
import { reachableTone, severityTone } from '@/lib/catalog';
import {
  ArtifactLoader, Bullets, ContractTooOld, DataTable, Metric, Section, ToneBadge, contractLabel,
  useArtifactJson,
} from './CatalogShared';

/**
 * Onglet « Parc » — inventaire distant du contrat 1.2
 * (`json/09_remote_inventory.json`, module 09 « collecteur central »).
 *
 * Ce que le client doit pouvoir lire ici : quel annuaire a été interrogé, quelles
 * machines existent, lesquelles ont été réellement inventoriées et par quel
 * moyen — et, surtout, **ce qui reste à faire** : les machines vues mais non
 * couvertes, chacune avec sa raison, sa piste et la commande à copier.
 *
 * Une machine jamais contactée s'affiche « non testé », jamais « injoignable ».
 */
export function FleetTab({ auditId, module, schemaVersion }: {
  auditId?: string;
  module?: DocumentData;
  schemaVersion?: string | null;
}) {
  const { t } = useT();
  const artifact = useArtifactJson(auditId, module as { json?: unknown; jsonPath?: string } | undefined);
  const fleet = useMemo(() => parseRemoteInventory(artifact.json), [artifact.json]);

  if (!fleet) {
    if (artifact.canLoad && artifact.path) {
      return (
        <ArtifactLoader
          path={artifact.path}
          label={t('audit.fleet.load')}
          loading={artifact.loading}
          error={artifact.error}
          onLoad={artifact.load}
        />
      );
    }
    return <ContractTooOld title={t('audit.fleet.unavailable.title')} schemaVersion={schemaVersion} />;
  }

  return (
    <div className="space-y-6">
      <CoverageSection fleet={fleet} />
      <DirectorySection fleet={fleet} />
      <HostsSection fleet={fleet} />
      <SharesSection fleet={fleet} />
      <SystemsSection fleet={fleet} />
      <FileRefsSection fleet={fleet} />
      <DeploymentPlanSection fleet={fleet} />
    </div>
  );
}

/** `—` pour une mesure absente, jamais un `0` inventé. */
const orDash = (n: number | null | undefined): string => (n == null ? '—' : n.toLocaleString());

function CoverageSection({ fleet }: { fleet: RemoteInventory }) {
  const { t } = useT();
  const { coverage } = fleet;
  const totals = remoteFileTotals(fleet);
  return (
    <Section
      title={t('audit.fleet.coverage')}
      description={t('audit.fleet.coverage.desc')}
      aside={coverage.methodsUsed.length > 0
        ? <span>{t('audit.fleet.methods', { list: coverage.methodsUsed.join(', ') })}</span>
        : undefined}
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Metric label={t('audit.fleet.hosts_total')} value={orDash(coverage.hostsTotal ?? fleet.hosts.length)} />
        <Metric label={t('audit.fleet.hosts_probed')} value={orDash(coverage.hostsProbed)} />
        <Metric label={t('audit.fleet.hosts_inventoried')} value={orDash(coverage.hostsInventoried)} />
        <Metric
          label={t('audit.fleet.hosts_skipped')}
          value={orDash(coverage.skipped.length)}
          sub={fleet.plan.length > 0 ? t('audit.fleet.to_deploy', { n: fleet.plan.length }) : undefined}
        />
        <Metric
          label={t('audit.fleet.files_remote')}
          value={totals.files.toLocaleString()}
          sub={fmtBytes(totals.bytes)}
        />
      </div>
      {coverage.budgetExhausted === true && (
        <div className="text-xs text-amber-700 dark:text-amber-400">{t('audit.fleet.budget_exhausted')}</div>
      )}
      {totals.truncated && (
        <div className="text-xs text-amber-700 dark:text-amber-400">{t('audit.fleet.truncated')}</div>
      )}
      {coverage.skipped.length > 0 && (
        <DataTable
          empty={t('audit.fleet.empty.skipped')}
          headers={[t('audit.fleet.col.host'), t('audit.fleet.col.reason'), t('audit.fleet.col.hint')]}
          rows={coverage.skipped.map(s => [
            <span className="break-all">{s.host}</span>,
            <ToneBadge tone={severityTone('warning')}>
              {contractLabel(t, 'audit.fleet.reason.' + (s.reason ?? ''), s.reason)}
            </ToneBadge>,
            s.hint ?? '—',
          ])}
        />
      )}
    </Section>
  );
}

function DirectorySection({ fleet }: { fleet: RemoteInventory }) {
  const { t } = useT();
  const { directory } = fleet;
  if (!directory.type && !directory.domain && directory.controllers.length === 0) return null;
  return (
    <Section title={t('audit.fleet.directory')} description={t('audit.fleet.directory.desc')}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Metric
          label={t('audit.fleet.directory.type')}
          value={contractLabel(t, 'audit.fleet.dir.' + (directory.type ?? ''), directory.type)}
        />
        <Metric label={t('audit.fleet.directory.domain')} value={directory.domain ?? '—'} />
        <Metric
          label={t('audit.fleet.directory.controllers')}
          value={`${directory.controllers.length}`}
          sub={directory.controllers.join(', ') || undefined}
        />
      </div>
    </Section>
  );
}

function HostsSection({ fleet }: { fleet: RemoteInventory }) {
  const { t } = useT();
  const untested = t('audit.catalog.rows_untested');
  return (
    <Section
      title={t('audit.fleet.hosts')}
      description={t('audit.fleet.hosts.desc')}
      aside={<span>{t('audit.fleet.hosts.count', { n: fleet.hosts.length })}</span>}
    >
      <DataTable
        empty={t('audit.fleet.empty.hosts')}
        headers={[
          t('audit.fleet.col.host'), t('audit.fleet.col.os'), t('audit.fleet.col.roles'),
          t('audit.fleet.col.source'), t('audit.fleet.col.reachable'), t('audit.fleet.col.probes'),
          t('audit.fleet.col.inventory'),
        ]}
        rows={fleet.hosts.map(h => [
          <span className="break-all" title={h.fqdn ?? undefined}>{h.name}</span>,
          h.os ?? '—',
          h.roles.length > 0 ? <Bullets items={h.roles} /> : '—',
          <span title={h.provenance?.evidence ?? undefined}>
            {contractLabel(t, 'audit.fleet.source.' + (h.source ?? ''), h.source)}
            {h.provenance?.collectedBy && (
              <span className="text-ink-500"> · {t('audit.fleet.observed_by', { host: h.provenance.collectedBy })}</span>
            )}
          </span>,
          <ToneBadge tone={reachableTone(h.reachable)}>
            {h.reachable === null ? untested
              : t(h.reachable ? 'audit.catalog.reachable.yes' : 'audit.catalog.reachable.no')}
          </ToneBadge>,
          h.probes.length > 0 ? h.probes.join(', ') : '—',
          h.inventory ? (
            <span>
              {contractLabel(t, 'audit.fleet.method.' + (h.inventory.method ?? ''), h.inventory.method)}
              {h.inventory.fileCount != null && (
                <span className="text-ink-500"> · {t('audit.catalog.files', { n: h.inventory.fileCount.toLocaleString() })}</span>
              )}
              {h.inventory.sizeBytes != null && <span className="text-ink-500"> · {fmtBytes(h.inventory.sizeBytes)}</span>}
              {h.inventory.truncated === true && <> <Badge variant="warning">{t('audit.fleet.truncated.badge')}</Badge></>}
            </span>
          ) : <span className="text-ink-500">{t('audit.fleet.no_inventory')}</span>,
        ])}
      />
    </Section>
  );
}

function SharesSection({ fleet }: { fleet: RemoteInventory }) {
  const { t } = useT();
  if (fleet.shares.length === 0) return null;
  return (
    <Section title={t('audit.fleet.shares')} description={t('audit.fleet.shares.desc')}>
      <DataTable
        empty={t('audit.fleet.empty.shares')}
        headers={[
          t('audit.fleet.col.host'), t('audit.fleet.col.share'), t('audit.fleet.col.path'),
          t('audit.fleet.col.protocol'), t('audit.fleet.col.acl'),
        ]}
        rows={fleet.shares.map(s => [
          s.host ?? '—',
          <span className="break-all">{s.name}</span>,
          <span className="break-all">{s.path ?? '—'}</span>,
          s.protocol ?? '—',
          s.acl.length > 0
            ? <Bullets items={s.acl.map(a => (a.rights.length > 0 ? `${a.principal} — ${a.rights.join(', ')}` : a.principal))} />
            : <span className="text-ink-500">{t('audit.fleet.acl.none')}</span>,
        ])}
      />
    </Section>
  );
}

function SystemsSection({ fleet }: { fleet: RemoteInventory }) {
  const { t } = useT();
  if (fleet.systems.length === 0) return null;
  return (
    <Section title={t('audit.fleet.systems')} description={t('audit.fleet.systems.desc')}>
      <DataTable
        empty={t('audit.fleet.empty.systems')}
        headers={[
          t('audit.fleet.col.host'), t('audit.fleet.col.cpu'), t('audit.fleet.col.ram'),
          t('audit.fleet.col.disks'), t('audit.fleet.col.os'), t('audit.fleet.col.method'),
        ]}
        rows={fleet.systems.map(r => [
          r.host ?? '—',
          r.cpu ?? '—',
          r.ram != null ? fmtBytes(r.ram) : '—',
          orDash(r.disks),
          r.os ?? '—',
          contractLabel(t, 'audit.fleet.method.' + (r.method ?? ''), r.method),
        ])}
      />
    </Section>
  );
}

function FileRefsSection({ fleet }: { fleet: RemoteInventory }) {
  const { t } = useT();
  if (fleet.fileRefs.length === 0) return null;
  return (
    <Section title={t('audit.fleet.file_refs')} description={t('audit.fleet.file_refs.desc')}>
      <DataTable
        empty={t('audit.fleet.empty.file_refs')}
        headers={[
          t('audit.fleet.col.host'), t('audit.fleet.col.share'), t('audit.fleet.col.files'),
          t('audit.fleet.col.size'), t('audit.fleet.col.method'), t('audit.fleet.col.member'),
        ]}
        rows={fleet.fileRefs.map(f => [
          f.host ?? '—',
          <span className="break-all">{f.share ?? '—'}</span>,
          <span>
            {orDash(f.fileCount)}
            {f.truncated === true && <> <Badge variant="warning">{t('audit.fleet.truncated.badge')}</Badge></>}
          </span>,
          f.sizeBytes != null ? fmtBytes(f.sizeBytes) : '—',
          contractLabel(t, 'audit.fleet.method.' + (f.method ?? ''), f.method),
          <code className="break-all text-[0.7rem]">{f.member ?? '—'}</code>,
        ])}
      />
    </Section>
  );
}

/**
 * Le livrable du module 09 : la liste des machines où une sonde locale reste
 * nécessaire, chacune avec la commande prête à copier.
 */
function DeploymentPlanSection({ fleet }: { fleet: RemoteInventory }) {
  const { t } = useT();
  if (fleet.plan.length === 0) {
    return (
      <Section title={t('audit.fleet.plan')} description={t('audit.fleet.plan.desc')}>
        <div className="text-sm text-ink-500">{t('audit.fleet.plan.none')}</div>
      </Section>
    );
  }
  return (
    <Section
      title={t('audit.fleet.plan')}
      description={t('audit.fleet.plan.desc')}
      aside={<span>{t('audit.fleet.plan.count', { n: fleet.plan.length })}</span>}
    >
      <ol className="space-y-3">
        {fleet.plan.map((action, i) => (
          <li key={`${action.host}-${i}`} className="rounded-lg border border-slate-200 p-3 dark:border-ink-700">
            <div className="flex flex-wrap items-center gap-2">
              <ServerCog className="h-4 w-4 shrink-0 text-brand-500" />
              <span className="font-medium text-ink-900 dark:text-white">{action.host}</span>
              {action.reason && (
                <Badge variant="warning">
                  {contractLabel(t, 'audit.fleet.reason.' + action.reason, action.reason)}
                </Badge>
              )}
            </div>
            {action.hint && <p className="mt-1 text-xs text-ink-500">{action.hint}</p>}
            <CommandRow command={action.command} />
          </li>
        ))}
      </ol>
    </Section>
  );
}

/** Commande à copier ; quand le toolkit n'en a pas fourni, on le dit plutôt que d'en inventer une. */
function CommandRow({ command }: { command: string | null }) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  if (!command) return <p className="mt-2 text-xs text-ink-500">{t('audit.fleet.plan.no_command')}</p>;
  const copy = async () => {
    await navigator.clipboard.writeText(command);
    setCopied(true);
    toast.success(t('audit.fleet.plan.copied'));
  };
  return (
    <div className="mt-2 flex items-center gap-2">
      <code className="min-w-0 flex-1 break-all rounded bg-slate-100 px-1.5 py-1 text-[0.75rem] dark:bg-ink-700">
        {command}
      </code>
      <Button variant="ghost" className="shrink-0" aria-label={t('audit.fleet.plan.copy')} onClick={copy}>
        <Copy className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{copied ? t('audit.fleet.plan.copied') : t('audit.fleet.plan.copy')}</span>
      </Button>
    </div>
  );
}

/**
 * Bandeau affiché quand la collecte n'a couvert qu'une partie du parc : c'est
 * une lacune de l'audit, pas un détail de mise en page.
 */
export function FleetCoverageBanner({ module }: { module?: DocumentData }) {
  const { t } = useT();
  const fleet = useMemo(
    () => parseRemoteInventory((module as { json?: unknown } | undefined)?.json),
    [module],
  );
  if (!fleet || fleet.plan.length === 0) return null;
  return (
    <AlertBanner variant="warning" icon={<ServerCog className="h-4 w-4" />} title={t('audit.fleet.banner.title')}>
      <div className="text-xs">
        {t('audit.fleet.banner.desc', {
          n: fleet.plan.length,
          hosts: fleet.plan.slice(0, 5).map(a => a.host).join(', '),
        })}
      </div>
    </AlertBanner>
  );
}
